import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { UserButton, SignedIn, SignedOut } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import FreakMeLogo from './FreakMeLogo'
import AuthModal from './AuthModal'
import ThirdwebWalletButton from './ThirdwebWalletButton'
import LanguageSwitcher from './LanguageSwitcher'
import { useAuth } from '../context/AuthContext'

const navLinks = [
  { key: 'nav.home', href: '/' },
  { key: 'nav.models', href: '/models' },
  { key: 'nav.categories', href: '/categories' },
  { key: 'nav.premium', href: '/premium' },
  { key: 'nav.creator', href: '/creator/apply' },
  { key: 'nav.affiliates', href: '/affiliate' },
  { key: 'nav.contact', href: '/contact' },
]

export default function Header() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login')
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const openLoginModal = () => {
    setAuthMode('login')
    setShowAuthModal(true)
  }

  const openSignupModal = () => {
    setAuthMode('signup')
    setShowAuthModal(true)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }
  }

  return (
    <>
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      <header className="site-header">
        <div className="header-top">
          <Link to="/" className="logo" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FreakMeLogo size={42} />
            <span>Freak<span style={{ color: 'var(--foreground)' }}>Me</span>.Fun</span>
          </Link>
          
          <div className="search-container">
            <form className="search-bar" onSubmit={handleSearch}>
              <input 
                type="text" 
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" title={t('search.placeholder')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
              </button>
            </form>
          </div>

          <div className="header-actions">
            <LanguageSwitcher />
            <ThirdwebWalletButton showBalance />
            
            <SignedIn>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Hi, {user?.name}
              </span>
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    avatarBox: {
                      width: '36px',
                      height: '36px',
                    },
                  },
                }}
              />
            </SignedIn>
            
            <SignedOut>
              <button className="header-btn btn-login" onClick={openLoginModal}>
                {t('auth.login')}
              </button>
              <button className="header-btn btn-signup" onClick={openSignupModal}>
                {t('auth.signup')}
              </button>
            </SignedOut>
          </div>
        </div>

        <nav className="main-nav">
          <div className="nav-container">
            {navLinks.map((link) => (
              <Link 
                key={link.key}
                to={link.href} 
                className={`nav-link ${location.pathname === link.href ? 'active' : ''}`}
              >
                {t(link.key)}
              </Link>
            ))}
          </div>
        </nav>
      </header>
    </>
  )
}
